import { WS_BASE } from '@/api/apiBase';
import { createOpQueue, type OpQueue } from './opQueue';
import type { Op } from './ops';

/** Minimal WebSocket surface — the browser `WebSocket` satisfies it, and tests
 *  inject a fake. */
export interface WebSocketLike {
  readyState: number;
  send(data: string): void;
  close(): void;
  addEventListener(type: string, listener: (ev: { data?: unknown }) => void): void;
}

export interface TripSyncHandlers {
  /** Full materialized trip received on (re)connect. */
  onHydrate(trip: unknown): void;
  /** An op broadcast from another client (already applied server-side). */
  onOp(op: Op): void;
}

export interface TripSyncClient {
  sendOp(op: Op): void;
  close(): void;
}

export interface TripSyncOptions {
  makeSocket?: (url: string) => WebSocketLike;
  queue?: OpQueue;
  /** Reconnect on unexpected close (default true). */
  reconnect?: boolean;
}

const WS_OPEN = 1;

/** Live collab client for one trip: opens a WebSocket to `/api/sync/:id`,
 *  surfaces the hydrate snapshot + incoming ops, and sends local ops (queueing
 *  them while offline, flushing on reconnect). */
export const createTripSync = (
  tripId: string,
  handlers: TripSyncHandlers,
  options: TripSyncOptions = {},
): TripSyncClient => {
  const queue = options.queue ?? createOpQueue(tripId);
  const wsUrl = `${WS_BASE}/api/sync/${tripId}`;
  const makeSocket =
    options.makeSocket ?? ((url) => new WebSocket(url) as unknown as WebSocketLike);
  let socket: WebSocketLike | null = null;
  let closed = false;

  const flush = (): void => {
    if (socket?.readyState !== WS_OPEN) return;
    for (const op of queue.drain()) socket.send(JSON.stringify({ type: 'op', op }));
  };

  const connect = (): void => {
    if (closed) return;
    socket = makeSocket(wsUrl);
    socket.addEventListener('open', () => flush());
    socket.addEventListener('message', (ev) => {
      let msg: { type?: string; trip?: unknown; op?: Op };
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      // A null `trip` means the server has no state for this id yet (a
      // brand-new trip whose create op hasn't landed) — nothing to hydrate.
      if (msg.type === 'hydrate' && msg.trip) handlers.onHydrate(msg.trip);
      else if (msg.type === 'op' && msg.op) handlers.onOp(msg.op);
    });
    socket.addEventListener('close', () => {
      if (!closed && options.reconnect !== false) setTimeout(connect, 1000);
    });
  };
  connect();

  return {
    sendOp: (op) => {
      if (socket?.readyState === WS_OPEN) socket.send(JSON.stringify({ type: 'op', op }));
      else queue.enqueue(op); // offline → queue; flushed on reconnect
    },
    close: () => {
      closed = true;
      socket?.close();
    },
  };
};
